import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, CheckCircle, RefreshCw, Trash2, Save } from 'lucide-react';
import { fetchSiniestCuarentena, corregirSiniestCuarentena, descartarSiniestCuarentena } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

function CuarentenaRow({ row, onSaved }) {
  const [draft, setDraft] = useState({
    fecha_incidente: row.fecha_incidente || '',
    fecha_baja: row.fecha_baja || '',
    fecha_alta: row.fecha_alta || '',
  });

  const saveMutation = useMutation({
    mutationFn: () => corregirSiniestCuarentena(row._qid, draft),
    onSuccess: (res) => {
      if (res.status === 'moved') {
        toast.success('Corregido: la fila ya cuenta en los KPIs de Siniestralidad');
        onSaved(row._qid);
      } else {
        toast.error(`Sigue sin ser válida: ${res.motivo}`);
      }
    },
    onError: () => toast.error('Error al guardar la corrección'),
  });

  const discardMutation = useMutation({
    mutationFn: () => descartarSiniestCuarentena(row._qid),
    onSuccess: () => { toast.success('Fila descartada'); onSaved(row._qid); },
    onError: () => toast.error('Error al descartar'),
  });

  return (
    <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid={`cuarentena-row-${row._qid}`}>
      <TableCell className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{row.persona || '—'}</TableCell>
      <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{row.codigo_ct || row.centro_trabajo || '—'}</TableCell>
      <TableCell>
        <Input value={draft.fecha_incidente || ''} onChange={e => setDraft(d => ({ ...d, fecha_incidente: e.target.value }))}
          placeholder="YYYY-MM-DD" className="h-7 text-xs w-28" data-testid={`cuarentena-fecha-incidente-${row._qid}`} />
      </TableCell>
      <TableCell>
        <Input value={draft.fecha_baja || ''} onChange={e => setDraft(d => ({ ...d, fecha_baja: e.target.value }))}
          placeholder="YYYY-MM-DD" className="h-7 text-xs w-28" data-testid={`cuarentena-fecha-baja-${row._qid}`} />
      </TableCell>
      <TableCell>
        <Input value={draft.fecha_alta || ''} onChange={e => setDraft(d => ({ ...d, fecha_alta: e.target.value }))}
          placeholder="YYYY-MM-DD" className="h-7 text-xs w-28" data-testid={`cuarentena-fecha-alta-${row._qid}`} />
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
          style={{ background: 'rgba(239,83,80,0.12)', color: '#EF5350' }}>
          {row._motivo_cuarentena}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex gap-1.5">
          <Button size="sm" className="h-7 text-xs gap-1" onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending} data-testid={`cuarentena-guardar-${row._qid}`}>
            {saveMutation.isPending ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
            Guardar
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => discardMutation.mutate()}
            disabled={discardMutation.isPending} data-testid={`cuarentena-descartar-${row._qid}`}>
            <Trash2 size={11} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function CuarentenaTab() {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['siniest-cuarentena'],
    queryFn: fetchSiniestCuarentena,
    staleTime: 30000,
  });
  const items = data?.items || [];

  const handleSaved = () => {
    qc.invalidateQueries(['siniest-cuarentena']);
    qc.invalidateQueries({ queryKey: ['siniest'] });
  };

  return (
    <div className="space-y-4" data-testid="cuarentena-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-montserrat font-semibold text-sm flex items-center gap-1.5" style={{ color: 'var(--vz-text-primary)' }}>
            <ShieldAlert size={14} style={{ color: '#EF5350' }} /> Cuarentena de Siniestralidad
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Filas del Excel importado con fechas imposibles (baja antes del incidente, fecha futura, etc.)
            que se excluyeron de los KPIs. Corrige la fecha y pulsa Guardar para que vuelvan a contar,
            o descártalas si son un error de origen.
          </p>
        </div>
        <button onClick={() => refetch()} className="p-1.5 rounded-lg transition-colors" style={{ background: 'var(--vz-bg-secondary)' }} data-testid="cuarentena-refresh">
          <RefreshCw size={14} style={{ color: 'var(--vz-text-muted)' }} />
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <CheckCircle size={32} style={{ color: 'var(--vz-green)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--vz-text-primary)' }}>Sin filas en cuarentena</p>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--vz-bg-secondary)', borderColor: 'var(--vz-border-subtle)' }}>
                <TableHead className="text-xs font-semibold">Persona</TableHead>
                <TableHead className="text-xs font-semibold">Centro</TableHead>
                <TableHead className="text-xs font-semibold">F. Incidente</TableHead>
                <TableHead className="text-xs font-semibold">F. Baja</TableHead>
                <TableHead className="text-xs font-semibold">F. Alta</TableHead>
                <TableHead className="text-xs font-semibold">Motivo</TableHead>
                <TableHead className="text-xs font-semibold">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(row => <CuarentenaRow key={row._qid} row={row} onSaved={handleSaved} />)}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
